/**
 * Funciones puras de cálculo para el módulo de Inteligencia de Negocios.
 * Utilizadas en múltiples páginas de reportes BI.
 */

/**
 * Formatea un número como moneda en colones costarricenses.
 * Para montos >= 1000: sin decimales (₡2.243.896)
 * Para montos < 1000: con 2 decimales (₡856,50)
 * @param {number|null|undefined} value
 * @returns {string}
 */
export function formatColones(value) {
  if (value == null) return "₡0";
  const num = Number(value);
  if (isNaN(num)) return "₡0";
  if (num === 0) return "₡0";
  const decimals = Math.abs(num) >= 1000 ? 0 : 2;
  return `₡${num.toLocaleString("es-CR", { minimumFractionDigits: decimals, maximumFractionDigits: decimals })}`;
}

/**
 * Formatea un valor como porcentaje con un decimal.
 * Ejemplo: 12.5 → "12.5%"
 * @param {number|string|null|undefined} value
 * @returns {string}
 */
export function formatPorcentaje(value) {
  if (value === "N/A") return "N/A";
  if (value == null) return "0.0%";
  return `${Number(value).toFixed(1)}%`;
}

/**
 * Formatea un string ISO de fecha a formato legible en español.
 * Ejemplo: "2024-01-15T10:30:00Z" → "15 ene 2024"
 * @param {string|null|undefined} dateStr
 * @returns {string}
 */
export function formatDate(dateStr) {
  if (!dateStr) return "—";
  const date = new Date(dateStr);
  if (isNaN(date.getTime())) return "—";
  return date.toLocaleDateString("es-CR", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

/**
 * Formatea un string ISO de fecha con hora.
 * Ejemplo: "2024-01-15T14:30:00Z" → "15/01/2024 14:30"
 * @param {string|null|undefined} dateStr
 * @returns {string}
 */
export function formatDateTime(dateStr) {
  if (!dateStr) return "—";
  const date = new Date(dateStr);
  if (isNaN(date.getTime())) return "—";
  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const year = date.getFullYear();
  const hours = String(date.getHours()).padStart(2, "0");
  const minutes = String(date.getMinutes()).padStart(2, "0");
  return `${day}/${month}/${year} ${hours}:${minutes}`;
}

/**
 * Formatea un número como moneda en colones en formato compacto para KPIs.
 * - < 10,000: ₡9.500
 * - 10,000 - 999,999: ₡31.165
 * - 1,000,000+: ₡2,24M (millones abreviados)
 * @param {number|null|undefined} value
 * @returns {string}
 */
export function formatColonesCompact(value) {
  if (value == null) return "₡0";
  const num = Number(value);
  if (isNaN(num)) return "₡0";
  if (num === 0) return "₡0";

  const abs = Math.abs(num);
  const sign = num < 0 ? "-" : "";

  if (abs >= 1000000) {
    const millions = abs / 1000000;
    const formatted = millions >= 10
      ? millions.toFixed(1)
      : millions.toFixed(2);
    return `${sign}₡${formatted.replace(".", ",")}M`;
  }

  // < 1,000,000: normal locale format without decimals
  const decimals = abs >= 1000 ? 0 : 2;
  return `${sign}₡${abs.toLocaleString("es-CR", { minimumFractionDigits: decimals, maximumFractionDigits: decimals })}`;
}

/**
 * Determina el estado de inventario de un producto.
 * @param {number} disponible - Inventario disponible actual
 * @param {number} minimo - Inventario mínimo configurado
 * @param {number} objetivo - Inventario objetivo configurado
 * @returns {'bajoMinimo'|'enObjetivo'|'sobreObjetivo'}
 */
export function calcularEstadoInventario(disponible, minimo, objetivo) {
  if (disponible < minimo) return "bajoMinimo";
  if (disponible > objetivo) return "sobreObjetivo";
  return "enObjetivo";
}

// Alias para compatibilidad con código existente
export const estadoInventario = calcularEstadoInventario;

/**
 * Retorna la clase de color Tailwind para un estado de inventario.
 * @param {'bajoMinimo'|'enObjetivo'|'sobreObjetivo'} estado
 * @returns {string}
 */
export function getEstadoColor(estado) {
  switch (estado) {
    case "bajoMinimo":
      return "text-red-600";
    case "sobreObjetivo":
      return "text-yellow-600";
    case "enObjetivo":
      return "text-green-600";
    default:
      return "text-gray-500";
  }
}

/**
 * Formatea una variación porcentual con signo.
 * Ejemplo: 12.5 → "+12.5%", -5.3 → "-5.3%", null → "N/A"
 * @param {number|string|null|undefined} variacion
 * @returns {string}
 */
export function formatVariacion(variacion) {
  if (variacion === "N/A" || variacion == null) return "N/A";
  const num = Number(variacion);
  if (isNaN(num)) return "N/A";
  const prefix = num > 0 ? "+" : "";
  return `${prefix}${num.toFixed(1)}%`;
}

/**
 * Determina la tendencia (trend) de una variación.
 * @param {number|string|null|undefined} variacion
 * @returns {'up'|'down'|'neutral'}
 */
export function getVariacionTrend(variacion) {
  if (variacion === "N/A" || variacion == null) return "neutral";
  const num = Number(variacion);
  if (isNaN(num)) return "neutral";
  if (num > 0) return "up";
  if (num < 0) return "down";
  return "neutral";
}
